import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import styled from '@emotion/styled';
import { ThumbsUp, Lock } from 'lucide-react';
import API_ENDPOINTS from '../config/api';
import { useInstanceId, useInstance } from '../contexts/InstanceContext';
import { useTranslation } from '../hooks/useTranslation';
import { getOrCreateParticipantId } from '../utils/participantId';
import socketService from '../../../../packages/shared/socket';
import { useScreenNavigation } from '../../../../packages/shared/useScreenNavigation';

/**
 * /kipupisteet — the room votes on the pain-point groups
 * (specs/prework-curation.md, "The room votes").
 *
 * Every device shows the groups the facilitator formed from the pre-work,
 * by scenario, with the participants' own sentences under each. A voter has
 * a budget of votes, one per group; counts climb live on every screen. The
 * facilitator opens and closes the vote from the Orchestrator; closed, the
 * screen shows the result. Nothing is promoted by a vote: the counts order
 * the facilitator's list.
 */

const Page = styled.div`
  min-height: 100vh;
  background: #0b0d12;
  color: #e8ecf2;
  padding: 2rem 1.25rem 4rem;
`;

const Inner = styled.div`
  max-width: 900px;
  margin: 0 auto;
  display: flex;
  flex-direction: column;
  gap: 1.25rem;
`;

const Head = styled.header`
  display: flex;
  flex-direction: column;
  gap: 0.35rem;
  h1 { margin: 0; font-size: 1.6rem; }
  p { margin: 0; color: #98a2b3; line-height: 1.5; }
`;

const Budget = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  align-self: flex-start;
  padding: 0.45rem 0.9rem;
  border-radius: 999px;
  background: ${(p) => (p.$left ? 'rgba(74, 158, 255, 0.14)' : 'rgba(255, 255, 255, 0.06)')};
  color: ${(p) => (p.$left ? '#8ec1f0' : '#98a2b3')};
  font-weight: 600;
  font-size: 0.95rem;
`;

const Scenario = styled.section`
  display: flex;
  flex-direction: column;
  gap: 0.6rem;
  h2 { margin: 0.4rem 0 0; font-size: 0.85rem; letter-spacing: 0.08em; text-transform: uppercase; color: #98a2b3; }
`;

const Card = styled.button`
  all: unset;
  cursor: ${(p) => (p.$disabled ? 'default' : 'pointer')};
  box-sizing: border-box;
  width: 100%;
  border-radius: 12px;
  border: 1px solid ${(p) => (p.$mine ? '#4a9eff' : '#232a36')};
  background: ${(p) => (p.$mine ? 'rgba(74, 158, 255, 0.10)' : '#141821')};
  padding: 1rem 1.1rem;
  display: grid;
  grid-template-columns: 1fr auto;
  gap: 0.5rem 1rem;
  align-items: center;
  opacity: ${(p) => (p.$disabled && !p.$mine ? 0.7 : 1)};
  &:focus-visible { outline: 2px solid #4a9eff; }
`;

const Title = styled.div`
  font-size: 1.1rem;
  font-weight: 600;
  line-height: 1.3;
`;

const Members = styled.ul`
  grid-column: 1 / -1;
  margin: 0;
  padding: 0 0 0 1.1rem;
  color: #98a2b3;
  font-size: 0.92rem;
  line-height: 1.45;
  li + li { margin-top: 0.15rem; }
`;

const Count = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 0.4rem;
  padding: 0.45rem 0.8rem;
  border-radius: 999px;
  font-weight: 700;
  font-size: 1rem;
  background: ${(p) => (p.$mine ? '#4a9eff' : 'rgba(255, 255, 255, 0.08)')};
  color: ${(p) => (p.$mine ? '#0b0d12' : '#e8ecf2')};
  min-width: 3.4rem;
  justify-content: center;
`;

const Muted = styled.p`
  margin: 0;
  color: #98a2b3;
`;

export default function PainPointVoteScreen() {
  const { t } = useTranslation();
  const [searchParams] = useSearchParams();
  const instanceId = useInstanceId(searchParams);
  const { currentInstance } = useInstance();
  useScreenNavigation();

  const participantId = getOrCreateParticipantId();
  const headers = participantId ? { 'X-Participant-Id': participantId } : undefined;
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);

  const load = useCallback(() => {
    if (!instanceId) return;
    fetch(API_ENDPOINTS.PREWORK_VOTES(instanceId), { credentials: 'include', headers })
      .then((r) => (r.ok ? r.json() : Promise.reject(new Error(`HTTP ${r.status}`))))
      .then(setData)
      .catch((e) => setError(e.message));
  }, [instanceId]); // eslint-disable-line react-hooks/exhaustive-deps
  // The open/closed switch is an instance setting with no event of its own,
  // so the screen re-reads every 15 s; votes themselves arrive by socket.
  useEffect(() => {
    load();
    const timer = setInterval(load, 15000);
    return () => clearInterval(timer);
  }, [load]);

  // Live counts, and the open/closed switch, as the facilitator and the room act.
  useEffect(() => {
    if (!instanceId) return undefined;
    socketService.connect(instanceId, 'display', `kipupisteet-${participantId || Date.now()}`);
    const onVotes = (msg) => { if (msg?.instanceId === instanceId) setData((d) => (d ? { ...d, counts: msg.counts || {} } : d)); };
    const onChange = () => load();
    socketService.socket?.on('prework-vote-updated', onVotes);
    socketService.socket?.on('instance-content-changed', onChange);
    return () => {
      socketService.socket?.off('prework-vote-updated', onVotes);
      socketService.socket?.off('instance-content-changed', onChange);
    };
  }, [instanceId, load]); // eslint-disable-line react-hooks/exhaustive-deps

  const spent = data ? Object.keys(data.myVotes || {}).length : 0;
  const left = data ? Math.max(0, data.budget - spent) : 0;

  const vote = async (clusterId) => {
    if (!data?.open || !data.canVote) return;
    const mine = !!data.myVotes[clusterId];
    if (!mine && left === 0) return;
    setError(null);
    try {
      const r = await fetch(API_ENDPOINTS.PREWORK_VOTE(instanceId, clusterId), { method: 'POST', credentials: 'include', headers });
      const next = await r.json();
      if (!r.ok) throw new Error(next.code === 'BUDGET' ? t('painPointVote.noVotesLeft') : next.code === 'CLOSED' ? t('painPointVote.closed') : next.error);
      setData((d) => ({ ...d, counts: next.counts, myVotes: next.myVotes }));
    } catch (e) {
      setError(e.message);
    }
  };

  const byScenario = useMemo(() => {
    if (!data) return [];
    const groups = new Map();
    for (const c of data.clusters) {
      const key = c.scenarioTitle || '';
      if (!groups.has(key)) groups.set(key, []);
      groups.get(key).push(c);
    }
    return [...groups.entries()];
  }, [data]);

  if (!instanceId) return <Page><Inner><Muted>{t('painPointVote.noInstance')}</Muted></Inner></Page>;
  if (!data && !error) return <Page><Inner><Muted>{t('me.loading')}</Muted></Inner></Page>;

  return (
    <Page>
      <Inner>
        <Head>
          <h1>{t('painPointVote.title')}</h1>
          <p>{currentInstance?.title ? `${currentInstance.title} · ` : ''}{data?.open ? t('painPointVote.intro', { budget: data.budget }) : t('painPointVote.closedIntro')}</p>
          {data?.open ? (
            <Budget $left={left > 0}><ThumbsUp size={16} /> {t('painPointVote.left', { left, budget: data.budget })}</Budget>
          ) : (
            <Budget><Lock size={15} /> {t('painPointVote.closed')}</Budget>
          )}
        </Head>
        {error && <Muted style={{ color: '#ff8a85' }}>{error}</Muted>}
        {data && !data.clusters.length && <Muted>{t('painPointVote.empty')}</Muted>}
        {byScenario.map(([scenario, clusters]) => (
          <Scenario key={scenario || 'none'}>
            {scenario && <h2>{scenario}</h2>}
            {clusters.map((c) => {
              const mine = !!data.myVotes[c.id];
              const disabled = !data.open || !data.canVote || (!mine && left === 0);
              return (
                <Card key={c.id} type="button" onClick={() => vote(c.id)} $mine={mine} $disabled={disabled} aria-pressed={mine} disabled={disabled}>
                  <Title>{c.title}</Title>
                  <Count $mine={mine}><ThumbsUp size={15} /> {data.counts[c.id] || 0}</Count>
                  <Members>{c.members.map((m, i) => <li key={i}>{m.text}</li>)}</Members>
                </Card>
              );
            })}
          </Scenario>
        ))}
      </Inner>
    </Page>
  );
}
